import { useState, useEffect } from 'react';
import api from '../../api';
import { ACTIVITY_TYPES } from './crmConstants';

// Log an interaction or create a task. Pass `customerId` to lock the customer,
// `defaults` to prefill (e.g. { type: 'task', opportunity_id }).
export default function ActivityModal({ activity, customerId, defaults = {}, onClose, onSaved }) {
  const editing = !!activity;
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    const base = activity || { customer_id: customerId || '', type: 'note', subject: '', ...defaults };
    return {
      customer_id: base.customer_id || '',
      type: base.type || 'note',
      subject: base.subject || '',
      description: base.description || '',
      due_date: base.due_date || '',
      contact_id: base.contact_id || '',
      opportunity_id: base.opportunity_id || '',
      quote_id: base.quote_id || '',
      assigned_to_id: base.assigned_to_id || '',
    };
  });

  const lockedCustomer = !!customerId || editing;

  useEffect(() => {
    api.get('/api/crm/users').then(r => setUsers(r.data));
    if (!lockedCustomer) api.get('/api/customers/').then(r => setCustomers(r.data));
  }, [lockedCustomer]);

  useEffect(() => {
    if (!form.customer_id) { setContacts([]); setOpportunities([]); setQuotes([]); return; }
    const cid = form.customer_id;
    api.get(`/api/crm/customers/${cid}/contacts`).then(r => setContacts(r.data));
    api.get(`/api/crm/opportunities?customer_id=${cid}`).then(r => setOpportunities(r.data));
    api.get(`/api/quotes/?customer_id=${cid}`).then(r => setQuotes(r.data));
  }, [form.customer_id]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const isTask = form.type === 'task';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      type: form.type,
      subject: form.subject,
      description: form.description || null,
      due_date: form.due_date || null,
      contact_id: form.contact_id ? parseInt(form.contact_id) : null,
      opportunity_id: form.opportunity_id ? parseInt(form.opportunity_id) : null,
      quote_id: form.quote_id ? parseInt(form.quote_id) : null,
      assigned_to_id: form.assigned_to_id ? parseInt(form.assigned_to_id) : null,
    };
    try {
      if (editing) {
        await api.put(`/api/crm/activities/${activity.id}`, payload);
      } else {
        await api.post('/api/crm/activities', { ...payload, customer_id: parseInt(form.customer_id) });
      }
      onSaved();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
        <h3 className="text-lg font-bold mb-4">{editing ? 'Aktiviteyi Düzenle' : isTask ? 'Yeni Görev' : 'Aktivite Kaydet'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-wrap gap-2 mb-4">
            {ACTIVITY_TYPES.map(t => (
              <button key={t.key} type="button" onClick={() => set('type', t.key)}
                className={`px-3 py-1 rounded-full text-sm border ${form.type === t.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {!lockedCustomer && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Müşteri *</label>
                <select required value={form.customer_id}
                  onChange={e => setForm(f => ({ ...f, customer_id: e.target.value, contact_id: '', opportunity_id: '', quote_id: '' }))}
                  className="w-full border rounded px-3 py-2">
                  <option value="">Seçiniz</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Konu *</label>
              <input required value={form.subject} onChange={e => set('subject', e.target.value)} className="w-full border rounded px-3 py-2"
                placeholder={isTask ? 'ör. Teklif için geri dönüş al' : 'ör. Satın alma ile görüşüldü'} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} className="w-full border rounded px-3 py-2" rows={3} />
            </div>
            {isTask && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Son Tarih</label>
                  <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Atanan</label>
                  <select value={form.assigned_to_id} onChange={e => set('assigned_to_id', e.target.value)} className="w-full border rounded px-3 py-2">
                    <option value="">{editing ? '-' : 'Ben'}</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kişi</label>
              <select value={form.contact_id} onChange={e => set('contact_id', e.target.value)} className="w-full border rounded px-3 py-2" disabled={!form.customer_id}>
                <option value="">-</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fırsat</label>
              <select value={form.opportunity_id} onChange={e => set('opportunity_id', e.target.value)} className="w-full border rounded px-3 py-2" disabled={!form.customer_id}>
                <option value="">-</option>
                {opportunities.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Teklif</label>
              <select value={form.quote_id} onChange={e => set('quote_id', e.target.value)} className="w-full border rounded px-3 py-2" disabled={!form.customer_id}>
                <option value="">-</option>
                {quotes.map(q => <option key={q.id} value={q.id}>{q.quote_number}{q.project_name ? ` · ${q.project_name}` : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">Kaydet</button>
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
          </div>
        </form>
      </div>
    </div>
  );
}
